import { DropdownMenu, Tooltip } from '@protoss/ui/components';
import * as React from 'react';

interface Project {
  id: string;
  name: string;
}

interface Domain {
  id: string;
  name: string;
  projects: Project[];
}

export interface OrganizationTreeProps {
  domains: Domain[];
  selectedDomainId?: string;
  selectedProjectId?: string;
  isWorkspaceAdmin?: boolean;
  onDomainSelect?: (domainId: string) => void;
  onProjectSelect?: (projectId: string) => void;
  onCreateDomain?: () => void;
  onCreateProject?: (domainId: string) => void;
}

/**
 * OrganizationTree - hierarchical domain/project tree for ContextPane
 *
 * Displays:
 * - "+ New Domain" button (workspace admin only)
 * - Domain (expandable/collapsible with edit button)
 *   - "+ New Project" button (authorized users)
 *   - Project (selectable)
 *   - Project (selectable)
 *
 * Behavior:
 * - Click domain to expand/collapse
 * - Click project to select
 * - Only one project can be selected at a time
 * - Selected project is highlighted
 * - Action buttons shown based on authorization
 *
 * Design system compliance:
 * - Uses semantic CSS variables
 * - Keyboard accessible
 * - Focus indicators
 */
export function OrganizationTree({
  domains,
  selectedDomainId,
  selectedProjectId,
  isWorkspaceAdmin = false,
  onDomainSelect,
  onProjectSelect,
  onCreateDomain,
  onCreateProject,
}: OrganizationTreeProps) {
  const [expandedDomains, setExpandedDomains] = React.useState<Set<string>>(
    new Set(domains.map((d) => d.id)) // All domains expanded by default
  );

  const toggleDomain = (domainId: string) => {
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domainId)) {
        next.delete(domainId);
      } else {
        next.add(domainId);
      }
      return next;
    });
  };

  const handleProjectClick = (projectId: string) => {
    if (onProjectSelect) {
      onProjectSelect(projectId);
    }
  };

  return (
    <div className="org-tree">
      <div className="org-tree-header">
        <h3 className="org-tree-title">Organization</h3>
        {isWorkspaceAdmin && (onCreateDomain || onCreateProject) && (
          <DropdownMenu.Root>
            <Tooltip.Simple label="Create domain or project">
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="org-tree-header-add-button"
                  aria-label="Create domain or project"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M8 3v10M3 8h10"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </DropdownMenu.Trigger>
            </Tooltip.Simple>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="org-tree-create-menu dropdown-content"
                side="right"
                align="start"
                sideOffset={4}
              >
                {onCreateDomain && (
                  <DropdownMenu.Item
                    className="org-tree-create-menu-item dropdown-item"
                    onSelect={onCreateDomain}
                  >
                    Create Domain
                  </DropdownMenu.Item>
                )}
                {onCreateProject && domains.length > 0 && (
                  <DropdownMenu.Item
                    className="org-tree-create-menu-item dropdown-item"
                    onSelect={() => {
                      // Create project without preselected domain
                      onCreateProject('');
                    }}
                  >
                    Create Project
                  </DropdownMenu.Item>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </div>
      {isWorkspaceAdmin && onCreateDomain && domains.length === 0 && (
        <div className="org-tree-actions">
          <Tooltip.Simple label="Create new domain">
            <button type="button" className="org-tree-add-domain-button" onClick={onCreateDomain}>
              + New Domain
            </button>
          </Tooltip.Simple>
        </div>
      )}
      <nav className="org-tree-nav" aria-label="Organization hierarchy">
        {domains.length === 0 ? (
          <div className="org-tree-empty">
            <p className="org-tree-empty-text">No domains yet</p>
            {isWorkspaceAdmin && onCreateDomain && (
              <p className="org-tree-empty-hint">Create a domain to get started</p>
            )}
          </div>
        ) : (
          domains.map((domain) => {
            const isExpanded = expandedDomains.has(domain.id);
            const isSelected = selectedDomainId === domain.id;
            return (
              <div key={domain.id} className="org-tree-domain">
                <div className="org-tree-domain-header">
                  <Tooltip.Simple label={isExpanded ? 'Collapse' : 'Expand'}>
                    <button
                      type="button"
                      className="org-tree-domain-toggle"
                      onClick={() => toggleDomain(domain.id)}
                      aria-expanded={isExpanded}
                    >
                      <svg
                        className="org-tree-chevron"
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        data-expanded={isExpanded || undefined}
                      >
                        <path
                          d="M4 2l4 4-4 4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </Tooltip.Simple>
                  <button
                    type="button"
                    className="org-tree-domain-button"
                    onClick={() => onDomainSelect?.(domain.id)}
                    data-selected={isSelected || undefined}
                  >
                    <span className="org-tree-domain-name">{domain.name}</span>
                  </button>
                </div>
                {isExpanded && (
                  <div className="org-tree-projects">
                    {isWorkspaceAdmin && onCreateProject && domain.projects.length === 0 && (
                      <Tooltip.Simple label="Create new project">
                        <button
                          type="button"
                          className="org-tree-add-project-button"
                          onClick={() => onCreateProject(domain.id)}
                        >
                          + New Project
                        </button>
                      </Tooltip.Simple>
                    )}
                    {domain.projects.length === 0 && !isWorkspaceAdmin ? (
                      <div className="org-tree-projects-empty">No projects</div>
                    ) : (
                      domain.projects.map((project) => {
                        const isSelected = selectedProjectId === project.id;
                        return (
                          <button
                            key={project.id}
                            type="button"
                            className="org-tree-project-button"
                            onClick={() => handleProjectClick(project.id)}
                            data-selected={isSelected || undefined}
                            aria-current={isSelected ? 'page' : undefined}
                          >
                            <span className="org-tree-project-name">{project.name}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </nav>
    </div>
  );
}
